import { type ScaleTime } from 'd3-scale';
import React, { type ReactNode } from 'react';

interface TimelineBlockProps {
  start: Date | number;
  timeScale: ScaleTime<number, number>;
  end?: Date | number;
  width?: number;
  className?: string;
  children?: ReactNode;
}

/**
 * A TimelineBlock represents a group of elements that are placed on a timeline.
 *
 * The simplest usage is where you specify a "timeScale" and a "start":
 *
 * ```
 * const timeScale = d3.scaleTime()
 *   .domain([new Date("2021-02-06T12:00:00.000Z"), new Date("2021-02-06T16:00:00.000Z")])
 *   .range([0, 400]);
 *
 * return (
 *   <svg width="400" height="10">
 *     <TimeBlock start={new Date("2021-02-06T13:00:00.000Z")} timeScale={timeScale}>
 *       <rect x="0" y="0" width="100" height="10" />
 *     </TimeBlock>
 *   </svg>
 * );
 * ```
 *
 * In the example above, the `rect` will be shown starting at 100 pixels from the
 * left edge of the SVG.  If we replace the `rect` with a component that returns
 * something compicated (like hundreds of event markers or dozens of histogram
 * bars), then we can reposition the content by changing the start or the timeScale
 * without having to re-render the content.
 *
 * There is also an optional `width` and `end` parameters which allow horizontal
 * scaling as well, which allows "zooming" the timeline, but note this works best
 * when the contents of your TimeBlock are boxes with a fill and no stroke.  If
 * you try this with circles, they'll quickly turn into elipses, which is
 * probably undesirable.
 *
 * Also note that it is not sufficient to fill a TimeBlock with hundreds of elements:
 *
 * ```
 * return (
 *   <svg width="400" height="10">
 *     <TimeBlock start={start} timeScale={timeScale}>
 *       { lotsOfEvents.map((event, index) =>
 *           <rect x={index * 10} y="0" width="10" height="10" />
 *       }
 *     </TimeBlock>
 *   </svg>
 * );
 * ```
 *
 * This will technically work, and will render correctly, but every time the
 * start or timeScale changes, we'll have to re-render all the `<rect>`s in the
 * array anyways, because they're part of this same component, so this is of
 * dubioua value from a performance perspective.  Instead use a PureComponent
 * or a `React.memo()`ed component inside the TimeBlock which takes `lotsOfEvents`
 * as a prop, and renders all the rects.  As long as `lotsOfEvents` doesn't change,
 * the child component will never re-render.
 */
const TimelineBlock: React.FC<TimelineBlockProps> = (props) => {
  const { timeScale, start, end, width, className } = props;

  const left = timeScale(start);
  let transform = `translate(${left}, 0)`;
  if (width !== undefined && end !== undefined) {
    const right = timeScale(end);
    const xScale = (right - left) / width;
    transform += ` scale(${xScale}, 1)`;
  }
  return (
    <g className={className} transform={transform} width={props.width}>
      {props.children}
    </g>
  );
};

export default React.memo(TimelineBlock);
